import React, { useEffect } from "react";
import classNames from "classnames";
import { ArrowUpRight, AudioLines } from "lucide-react";
import { Link } from "react-router-dom";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { fetchAndUpdateMemoryAction } from "../../../state/memories/actions";
import { Memory as MemoryType } from "../../../state/memories/types";
import { getMemoryDetailPath } from "../../../Router/routes";
import { State } from "../../../state/state";
import { formatRecordingDuration } from "../../../state/memories/utils";
import styles from "../styles.stylesheet.module.scss";

interface MemoryProps {
  item: MemoryType;
}

const formatTimeLabel = (dateValue: string) => {
  return new Date(dateValue).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getCardDescription = (item: MemoryType) => {
  if (item.is_processing) {
    return "Processing transcript, summary, and recommended questions.";
  }

  return (
    item.summary?.quick_summary ||
    "Open this memory to explore its transcript and summary."
  );
};

export const Memory = ({ item }: MemoryProps) => {
  const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>();

  useEffect(() => {
    if (!item.is_processing) return;

    const interval = window.setInterval(() => {
      dispatch(fetchAndUpdateMemoryAction(item.id, { force: true }));
    }, 5000);

    return () => window.clearInterval(interval);
  }, [item.id, item.is_processing, dispatch]);

  return (
    <Link
      className={classNames(styles.memoryCard, {
        [styles.memoryCardDisabled]: item.is_processing,
      })}
      to={getMemoryDetailPath(item.id)}
      aria-disabled={item.is_processing}
      onClick={(event) => {
        if (item.is_processing) event.preventDefault();
      }}
    >
      <div className={styles.memoryCardBody}>
        <div className={styles.memoryCardTopline}>
          <span className={styles.memoryTime}>
            {formatTimeLabel(item.started_at)}
          </span>
          <span className={styles.memoryDuration}>
            {formatRecordingDuration(item.duration_seconds)}
          </span>
        </div>
        <h3
          className={classNames({
            [styles.memoryProcessingTitle]: item.is_processing,
          })}
        >
          {item.is_processing ? "Processing" : item.name}
        </h3>
        <p className={styles.memoryDescription}>{getCardDescription(item)}</p>
        <span className={styles.memoryCardAction}>
          <AudioLines aria-hidden="true" size={17} strokeWidth={1.9} />
          <ArrowUpRight aria-hidden="true" size={18} strokeWidth={1.8} />
        </span>
      </div>
    </Link>
  );
};
